/* routine-planner.js
   Data source: /assets/routine-products.json

   Omnisend Embedded Form:
   - Global mount must NOT use display:none (use offscreen parking instead)
   - JS moves the embed div into step 6 slot, then parks it offscreen again

   Step 6 UX:
   - Omnisend submit button renamed to "Continue"
   - Continue disabled until email valid + consent checkbox checked
   - Skip is our button (goes to results)

   Logic fixes:
   - HARD de-dupe of results: never show same product twice (by handle/url)
   - Match % always unique within results
   - Results ORDER is by score (logic), not by percent (percent is display-only)

   Omnisend events:
   - quiz_answers_submitted (on Continue)
   - quiz_completed (after calc) with recommended_name_1..3 / recommended_product_1..3
     + recommended_price_1..3 / recommended_image_1..3
*/
(() => {
  "use strict";

  const DATA_ASSET_URL = (window.RP_PRODUCTS_URL || "/assets/routine-products.json");
  const OMNISEND_EMBED_ID = "omnisend-embedded-v2-69b1c4ef3950c1359b3aa895";
  const OMNI_GLOBAL_MOUNT_ID = "rp-omni-global-mount";
  const QUIZ_ID = "herbal_routine_planner";

  // ---------- QUIZ TAXONOMY ----------
  const GOALS = [
    { id: "calm", i: "😌", l: "Stress & Calm", d: "Support a calm, balanced day" },
    { id: "sleep", i: "😴", l: "Sleep Routine", d: "Support a restful bedtime routine" },
    { id: "energy", i: "⚡", l: "Energy & Stamina", d: "Support steady daily energy" },
    { id: "focus", i: "🧠", l: "Focus & Clarity", d: "Support mental clarity for work or study" },
    { id: "gut", i: "🍃", l: "Gut & Digestion", d: "Support digestive comfort and balance" },
    { id: "immune", i: "🛡️", l: "Immune Support", d: "Support everyday wellness, year-round" },
    { id: "mobility", i: "🏃", l: "Joints & Mobility", d: "Support comfortable movement and recovery" },
    { id: "skin", i: "✨", l: "Skin & Hair", d: "Support healthy-looking skin and hair" }
  ];

  const LIFESTYLES = [
    { id: "desk", i: "💻", l: "Desk Worker", d: "Long hours, lots of screen time" },
    { id: "parent", i: "👨‍👧", l: "Busy Parent", d: "Always on the go, little downtime" },
    { id: "athlete", i: "🏋️", l: "Active / Athlete", d: "Regular workouts, need recovery" },
    { id: "student", i: "📚", l: "Student", d: "Study, exams, deadlines" },
    { id: "shift", i: "🌙", l: "Shift Worker", d: "Irregular hours or night shifts" },
    { id: "40plus", i: "💼", l: "40+ Professional", d: "Time-poor, want simple consistency" }
  ];

  const TIMINGS = [
    { id: "am", i: "🌅", l: "Morning", d: "Easy to take in the morning" },
    { id: "pm", i: "🌙", l: "Evening", d: "Fits a wind-down routine" },
    { id: "both", i: "🔄", l: "Morning + Evening", d: "Flexible — I can do both" },
    { id: "any", i: "🤷", l: "Flexible timing", d: "I'll fit it in when I can" }
  ];

  const COMMITS = [
    { id: "minimal", i: "☝️", l: "Keep it simple", d: "1 product per day — easy to stick with" },
    { id: "standard", i: "✌️", l: "A real routine", d: "2 products per day — a solid baseline" },
    { id: "full", i: "🤟", l: "I'm all in", d: "3 products per day — a complete routine" }
  ];

  const FORMATS = [
    { id: "tincture", i: "💧", l: "Tinctures", d: "Liquid herbal extracts" },
    { id: "capsule", i: "💊", l: "Capsules", d: "Convenient, travel-friendly" },
    { id: "blend", i: "🌿", l: "Formulas", d: "Multi-herb formulas" },
    { id: "any", i: "👍", l: "No Preference", d: "Pick what fits best" }
  ];

  const GL = {
    calm: "Stress & Calm",
    sleep: "Sleep Routine",
    energy: "Energy & Stamina",
    focus: "Focus & Clarity",
    gut: "Gut & Digestion",
    immune: "Immune Support",
    mobility: "Joints & Mobility",
    skin: "Skin & Hair",
    hair: "Hair Health",
    libido: "Intimate Wellness",
    respiratory: "Respiratory",
    liver: "Liver Health",
    heart: "Heart Health",
    urinary: "Urinary Health",
    hormones: "Hormone Balance"
  };

  const FL = { t: "Tincture", c: "Capsules", b: "Formula" };
  const TL = { a: "☀️ Best in the morning", p: "🌙 Best in the evening", b: "🔄 Morning or evening" };

  const LG = {
    desk: ["focus", "calm", "mobility"],
    parent: ["energy", "calm", "immune"],
    athlete: ["energy", "mobility", "immune"],
    student: ["focus", "energy", "calm"],
    shift: ["sleep", "energy", "calm"],
    "40plus": ["energy", "gut", "heart"]
  };

  // ---------- DATA ----------
  let PRODUCTS = [];

  // ---------- SHOPIFY META + CART ----------
  const PRODUCT_CACHE = new Map(); // handle -> { img, price }
  const VARIANT_CACHE = new Map(); // handle -> variantId

  function esc(s) {
    return String(s).replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[m]));
  }
  function qs(sel, root = document) { return root.querySelector(sel); }

  function getHandle(url) {
    const m = String(url).match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function productKey(p){
    const h = getHandle(p.u);
    if (h) return `h:${h}`;
    return `u:${String(p.u || '').trim().toLowerCase()}`;
  }

  async function getProductJson(handle) {
    const r = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
    if (!r.ok) throw new Error("Product JSON not found");
    return r.json();
  }

  async function getProductMetaByHandle(handle) {
    if (!handle) throw new Error("Missing handle");
    if (PRODUCT_CACHE.has(handle)) return PRODUCT_CACHE.get(handle);

    const pj = await getProductJson(handle);
    const img = pj?.featured_image || (Array.isArray(pj?.images) ? pj.images[0] : null) || null;
    const price = typeof pj?.price === "number" ? pj.price / 100 : null;

    const meta = { img, price };
    PRODUCT_CACHE.set(handle, meta);
    return meta;
  }

  async function getVariantIdByHandle(handle) {
    if (!handle) throw new Error("Missing handle");
    if (VARIANT_CACHE.has(handle)) return VARIANT_CACHE.get(handle);

    const pj = await getProductJson(handle);
    const variants = Array.isArray(pj.variants) ? pj.variants : [];
    const v = variants.find((x) => x && x.available) || variants[0];
    if (!v || !v.id) throw new Error("No variant");

    VARIANT_CACHE.set(handle, v.id);
    return v.id;
  }

  async function shopifyAddToCart(variantId, qty = 1) {
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
      credentials: "same-origin"
    });
    if (!r.ok) throw new Error("Add to cart failed");
    return r.json();
  }

  // ---------- ANALYTICS ----------
  function getAnonId() {
    const k = "rp_uid_v1";
    try {
      let v = localStorage.getItem(k);
      if (!v) {
        v = globalThis.crypto?.randomUUID?.() || String(Math.random()).slice(2);
        localStorage.setItem(k, v);
      }
      return v;
    } catch { return "na"; }
  }

  function pushDL(event, params) {
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event, ...params }); } catch {}
    try { if (typeof window.gtag === "function") window.gtag("event", event, params); } catch {}
  }

  function buildRpUtm(ans) {
    const uid = getAnonId();
    const p = new URLSearchParams({
      utm_source: "quiz",
      utm_medium: "onsite",
      utm_campaign: "routine_planner",
      utm_content: `${ans.goal || "na"}_${ans.lifestyle || "na"}_${ans.timing || "na"}_${ans.format || "na"}_${ans.commit || "na"}`,
      rp_uid: uid
    });
    return p.toString();
  }

  function withUtm(url, utm) {
    try {
      const u = new URL(url, window.location.origin);
      const add = new URLSearchParams(utm);
      for (const [k, v] of add.entries()) if (!u.searchParams.has(k)) u.searchParams.set(k, v);
      return u.toString();
    } catch { return url + (url.includes("?") ? "&" : "?") + utm; }
  }

  // ---------- OMNISEND API HELPERS ----------
  function omniPushSafe(arr){
    try{
      window.omnisend = window.omnisend || [];
      window.omnisend.push(arr);
      return true;
    }catch{ return false; }
  }
  function omniIdentify(email, props){
    if (!email) return false;
    return omniPushSafe(['identifyContact', { email, customProperties: props }]);
  }
  function omniTrack(eventName, properties){
    return omniPushSafe(['track', eventName, { origin: 'api', properties }]);
  }

  function readOmniEmailFromStep(root){
    const slot = root.querySelector('#rp-omni-slot');
    if (!slot) return '';
    const inp =
      slot.querySelector('input[type="email"]') ||
      slot.querySelector('input[name*="email" i]') ||
      slot.querySelector('input[name="emailField"]');
    return (inp?.value || '').trim();
  }

  // ---------- STRICT FILTERS ----------
  function timingAllows(p, timing) {
    if (timing === "am") return p.t === "a" || p.t === "b";
    if (timing === "pm") return p.t === "p" || p.t === "b";
    return true;
  }
  function formatAllows(p, format) {
    if (format === "capsule") return p.f === "c";
    if (format === "tincture") return p.f === "t";
    if (format === "blend") return p.f === "b";
    return true;
  }

  function isExcludedProduct(p){
    const name = String(p.n || '').toLowerCase();
    const url  = String(p.u || '').toLowerCase();
    if (name.includes('gallon')) return true;
    if (name.includes('bulk')) return true;
    if (name.includes('wholesale')) return true;
    if (name.includes('1 gal') || name.includes('1-gal')) return true;
    if (url.includes('gallon') || url.includes('bulk') || url.includes('wholesale')) return true;
    return false;
  }

  // ---------- MATCH % ----------
  function hashStr(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }

  function attachMatchPercents(results) {
    if (!results.length) return;

    const scores = results.map((x) => x.score);
    const max = Math.max(...scores);
    const min = Math.min(...scores);

    const d = new Date();
    const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    results.forEach((p, i) => {
      let base;
      if (max === min) base = [92, 86, 81][i] ?? 78;
      else {
        const norm = (p.score - min) / (max - min);
        base = 76 + norm * 20;
        if (i === 0) base = Math.max(base, 92);
        if (i === 1) base = Math.min(Math.max(base, 84), 91);
        if (i === 2) base = Math.min(Math.max(base, 78), 88);
      }
      const jitter = (hashStr(p.n + dayKey) % 5) - 2;
      p.matchPct = Math.round(Math.min(98, Math.max(72, base + jitter)));
    });

    // enforce uniqueness strictly
    const used = new Set();
    for (const p of results) {
      let pct = Math.min(98, Math.max(72, p.matchPct));
      while (used.has(pct) && pct > 72) pct -= 1;
      while (used.has(pct) && pct < 98) pct += 1;
      while (used.has(pct) && pct > 72) pct -= 1;
      p.matchPct = pct;
      used.add(pct);
    }
  }

  function weightedSampleUnique(pool, count, bannedSet) {
    const chosen = [];
    const candidates = pool.filter((p) => !bannedSet.has(p.n));
    if (!candidates.length) return chosen;

    while (chosen.length < count && candidates.length) {
      const weights = candidates.map((p) => Math.pow(Math.max(1, p.score), 1.8));
      const sum = weights.reduce((a, b) => a + b, 0);
      let r = Math.random() * sum;

      let idx = 0;
      for (let i = 0; i < weights.length; i++) { r -= weights[i]; if (r <= 0) { idx = i; break; } }
      chosen.push(candidates[idx]);
      candidates.splice(idx, 1);
    }
    return chosen;
  }

  function calc(ans) {
    const sec = LG[ans.lifestyle] || [];
    const candidates = PRODUCTS.filter(p =>
      timingAllows(p, ans.timing) &&
      formatAllows(p, ans.format) &&
      !isExcludedProduct(p)
    );
    if (!candidates.length) return [];

    const scored = candidates
      .map((p) => {
        let s = 0;
        if (p.g.includes(ans.goal)) s += 100;
        sec.forEach((sg) => { if (p.g.includes(sg)) s += 15; });
        s += p.g.filter((g) => g === ans.goal || sec.includes(g)).length * 5;
        return { ...p, score: s };
      })
      .sort((a, b) => b.score - a.score);

    const mx = { minimal: 1, standard: 2, full: 3 }[ans.commit] || 2;

    const topScore = scored[0]?.score ?? 0;
    const K = 24;
    const WINDOW = 40;

    const poolRaw = scored.filter((p) => p.score >= Math.max(30, topScore - WINDOW)).slice(0, K);

    // HARD de-dupe pool by handle/url
    const seenKey = new Set();
    const pool = [];
    for (const p of poolRaw) {
      const k = productKey(p);
      if (seenKey.has(k)) continue;
      seenKey.add(k);
      pool.push(p);
    }

    const ansKey = JSON.stringify([ans.goal, ans.lifestyle, ans.timing, ans.commit, ans.format]);
    const LS_KEY = "rp_seen_v_omni";
    const now = Date.now();
    const TTL = 1000 * 60 * 60 * 24 * 7;

    let seen = {};
    try { seen = JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {}; } catch {}
    if (!seen[ansKey]) seen[ansKey] = [];
    seen[ansKey] = (seen[ansKey] || []).filter((x) => now - (x.ts || 0) < TTL);
    const recentlyShown = new Set(seen[ansKey].map((x) => x.name));

    let pick = weightedSampleUnique(pool, mx, recentlyShown);
    if (pick.length < mx) pick = pick.concat(weightedSampleUnique(pool, mx - pick.length, new Set()));

    // Final HARD de-dupe (by handle/url)
    const finalSeen = new Set();
    pick = pick.filter((p) => {
      const k = productKey(p);
      if (finalSeen.has(k)) return false;
      finalSeen.add(k);
      return true;
    });

    // ORDER strictly by score (logic)
    pick.sort((a, b) => b.score - a.score || a.n.localeCompare(b.n));

    // percent is display-only
    attachMatchPercents(pick);

    for (const p of pick) seen[ansKey].push({ name: p.n, ts: now });
    if (seen[ansKey].length > 50) seen[ansKey] = seen[ansKey].slice(-50);
    try { localStorage.setItem(LS_KEY, JSON.stringify(seen)); } catch {}

    return pick;
  }

  // ---------- UI STATE ----------
  const state = {
    step: 0,
    ans: { goal: null, lifestyle: null, timing: null, commit: null, format: null },
    results: [],
    lastEmail: "",
    addedMap: Object.create(null),
    busyMap: Object.create(null),
    errorMap: Object.create(null),
    dataError: ""
  };

  const cL = { minimal: "1 product", standard: "2 products", full: "3 products" };
  const tMap = { am: "Morning ☀️", pm: "Evening 🌙", both: "AM + PM", any: "Flexible timing" };
  const fMap = { tincture: "Tinctures", capsule: "Capsules", blend: "Formulas", any: "No Preference" };

  function pct(step) {
    const total = 6;
    const idx = Math.min(total, Math.max(0, step));
    return Math.min(100, Math.round((idx / total) * 100));
  }

  // ---------- MOBILE-ONLY AUTO SCROLL ----------
  let shouldAutoScroll = false;
  let step0Scrolled = false;

  function isMobile() { return window.matchMedia && window.matchMedia("(max-width: 768px)").matches; }

  function scrollToIdMobile(id) {
    if (!isMobile()) return;
    const el = document.getElementById(id);
    if (!el) return;

    const rpSticky = document.querySelector(".rp-sticky");
    const offset = (rpSticky ? rpSticky.getBoundingClientRect().height : 0) + 12;

    const y = window.pageYOffset + el.getBoundingClientRect().top - offset;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  }

  // ---------- OMNISEND EMBED HELPERS ----------
  function getOmniNode() { return document.getElementById(OMNISEND_EMBED_ID); }
  function getOmniGlobalMount() { return document.getElementById(OMNI_GLOBAL_MOUNT_ID); }

  function ensureGlobalMountVisibleNotDisplayNone(){
    const mount = getOmniGlobalMount();
    if (!mount) return;

    mount.style.display = "block"; // NEVER display:none
    mount.style.position = "absolute";
    mount.style.left = "-9999px";
    mount.style.top = "auto";
    mount.style.width = "1px";
    mount.style.height = "1px";
    mount.style.overflow = "hidden";
  }

  function clearOffscreenStyles(el){
    if (!el) return;
    el.style.display = "";
    el.style.position = "";
    el.style.left = "";
    el.style.top = "";
    el.style.width = "";
    el.style.height = "";
    el.style.overflow = "";
  }

  function moveOmniToSlot(root) {
    const node = getOmniNode();
    const slot = root.querySelector("#rp-omni-slot");
    if (!node || !slot) return;

    clearOffscreenStyles(node);
    if (!slot.contains(node)) slot.appendChild(node);
  }

  function moveOmniBackToGlobal() {
    const node = getOmniNode();
    const mount = getOmniGlobalMount();
    if (!node || !mount) return;

    ensureGlobalMountVisibleNotDisplayNone();
    if (!mount.contains(node)) mount.appendChild(node);
  }

  function findOmniSubmit(slot) {
    if (!slot) return null;
    return (
      slot.querySelector('button[type="submit"]') ||
      slot.querySelector('input[type="submit"]') ||
      [...slot.querySelectorAll("button")].find((b) => (b.textContent || "").toLowerCase().includes("see my results")) ||
      slot.querySelector("button") ||
      null
    );
  }

  function tweakOmniButton(root) {
    const slot = root.querySelector("#rp-omni-slot");
    if (!slot) return;

    const btn = findOmniSubmit(slot);
    if (!btn) return;

    if (btn.tagName === "INPUT") btn.value = "Continue";
    else btn.textContent = "Continue";

    btn.classList.add("rp-omni-continue");
    btn.disabled = true;
    btn.setAttribute("aria-disabled", "true");
  }

  function omniIsReady(root) {
    const slot = root.querySelector("#rp-omni-slot");
    if (!slot) return false;

    const emailInput = slot.querySelector('input[type="email"], input[name*="email" i], input[name="emailField"]');
    const consentBox = slot.querySelector('input[type="checkbox"]');

    const email = (emailInput?.value || "").trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const consentOk = !!consentBox?.checked;

    return emailOk && consentOk;
  }

  function bindOmniSubmitGate(root) {
    const slot = root.querySelector("#rp-omni-slot");
    if (!slot) return;

    const update = () => {
      const btn = findOmniSubmit(slot);
      if (!btn) return;

      const ok = omniIsReady(root);
      btn.disabled = !ok;
      btn.setAttribute("aria-disabled", ok ? "false" : "true");
      btn.style.opacity = ok ? "" : "0.6";
      btn.style.cursor = ok ? "pointer" : "not-allowed";
    };

    slot.addEventListener("input", update, true);
    slot.addEventListener("change", update, true);

    slot.addEventListener("click", (e) => {
      const btn = findOmniSubmit(slot);
      if (!btn) return;

      const t = e.target;
      const clickedBtn = (t === btn) || (t && t.closest && t.closest("button") === btn);
      if (!clickedBtn) return;

      if (!btn.disabled) {
        const uid = getAnonId();
        const email = readOmniEmailFromStep(root);
        state.lastEmail = email;

        // Identify + store custom properties on the contact
        omniIdentify(email, {
          rp_goal: state.ans.goal,
          rp_lifestyle: state.ans.lifestyle,
          rp_timing: state.ans.timing,
          rp_commit: state.ans.commit,
          rp_format: state.ans.format,
          rp_uid: uid,
          rp_page: String(location.href || '')
        });

        // Track: answers submitted (email step)
        omniTrack("quiz_answers_submitted", {
          quiz_id: QUIZ_ID,
          rp_uid: uid,
          answers: {
            goal: state.ans.goal,
            lifestyle: state.ans.lifestyle,
            timing: state.ans.timing,
            commit: state.ans.commit,
            format: state.ans.format
          }
        });

        pushDL("rp_email_submit", { rp_uid: uid });

        // Continue to results
        setTimeout(() => go(7), 900);
      }
    }, true);

    update();
    setTimeout(update, 300);
    setTimeout(update, 1200);
  }

  // ---------- Helpers for quiz_completed payload (price + image) ----------
  function toAbsUrl(url){
    const s = String(url || '').trim();
    if (!s) return '';
    if (s.startsWith('//')) return 'https:' + s;
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    try { return new URL(s, window.location.origin).toString(); } catch { return s; }
  }

  function moneyUSD(n){
    const v = Number(n);
    if (!Number.isFinite(v)) return '';
    return `$${v.toFixed(2)}`;
  }

  async function buildQuizCompletedPayload(uid){
    const rec = (state.results || []).slice(0, 3);

    const payload = {
      quiz_id: QUIZ_ID,
      rp_uid: uid,
      answers: {
        goal: state.ans.goal,
        lifestyle: state.ans.lifestyle,
        timing: state.ans.timing,
        commit: state.ans.commit,
        format: state.ans.format
      }
    };

    for (let i = 0; i < rec.length; i++){
      const p = rec[i];
      const idx = i + 1;

      const handle = getHandle(p.u) || "";
      payload[`recommended_name_${idx}`] = p.n || "";
      payload[`recommended_product_${idx}`] = handle;

      if (handle){
        try{
          const meta = await getProductMetaByHandle(handle); // {img, price}
          if (meta?.img) payload[`recommended_image_${idx}`] = toAbsUrl(meta.img);
          if (typeof meta?.price === 'number') payload[`recommended_price_${idx}`] = moneyUSD(meta.price);
        }catch{
          // ignore
        }
      }
    }

    return payload;
  }

  // ---------- RENDER ----------
  function render() {
    const root = qs("#herbera-routine-root");
    if (!root) return;

    const st = state.step;
    const shownStep = st >= 1 && st <= 6 ? st : st === 7 ? 6 : st;
    const p = pct(shownStep);

    root.innerHTML = `
      <div class="rp-app">
        <div class="rp-hero">
          <h1>Herbal Routine Planner</h1>
          <p>Answer a few questions — get a personalized routine based on your preferences</p>
        </div>

        ${st >= 1 && st <= 7 ? `
          <div class="rp-sticky">
            <div class="rp-sticky-inner">
              <div class="rp-sticky-top">
                <span class="rp-step">${st <= 6 ? `Step ${st} of 6` : "Calculating..."}</span>
                <span class="rp-pct">${p}%</span>
              </div>
              <div class="rp-bar"><div class="rp-bar-fill" style="width:${p}%"></div></div>
            </div>
          </div>
        ` : ""}

        <div class="rp-container">
          ${renderStep()}
        </div>
      </div>
    `;

    wireEvents(root);

    if (isMobile() && state.step === 0 && !step0Scrolled) {
      step0Scrolled = true;
      requestAnimationFrame(() => requestAnimationFrame(() => scrollToIdMobile("rp-step0-h2")));
    }

    if (isMobile() && shouldAutoScroll && state.step >= 1 && state.step <= 6) {
      shouldAutoScroll = false;
      requestAnimationFrame(() => requestAnimationFrame(() => scrollToIdMobile("rp-step-h2")));
    }

    if (state.step === 6) {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        moveOmniToSlot(root);
        tweakOmniButton(root);
        bindOmniSubmitGate(root);
      }));
    } else {
      requestAnimationFrame(() => requestAnimationFrame(() => moveOmniBackToGlobal()));
    }
  }

  function renderStep() {
    const st = state.step;

    if (state.dataError) {
      return `
        <div class="rp-stepwrap">
          <div class="rp-stephead">
            <h2 id="rp-step-h2">Data not loaded</h2>
            <p>${esc(state.dataError)}</p>
          </div>
          <div class="rp-formcard">
            <div class="rp-form-err">Upload <strong>routine-products.json</strong> to theme Assets, then reload this page.</div>
            <div class="rp-formactions">
              <button class="rp-primary-sm" data-action="retry_data">Retry</button>
            </div>
          </div>
        </div>
      `;
    }

    if (st === 0) {
      return `
        <div class="rp-start">
          <h2 id="rp-step0-h2">Build Your Personal Herbal Routine</h2>
          <p class="rp-lead">Tell us what you want to support and how your days look. We’ll match you with products from our collection.</p>

          <div class="rp-features">
            ${[
              { i: "🎯", t: "Preference-Based", d: "Matched to your goal, lifestyle, and schedule" },
              { i: "⏱️", t: "Quick & Simple", d: "6 short steps, instant suggestions" },
              { i: "🌿", t: "Quality-First", d: "Made with careful sourcing and standards" }
            ].map(f => `
              <div class="rp-feature">
                <div class="rp-feature-ico">${f.i}</div>
                <div>
                  <div class="rp-feature-title">${esc(f.t)}</div>
                  <div class="rp-feature-desc">${esc(f.d)}</div>
                </div>
              </div>
            `).join("")}
          </div>

          <button class="rp-primary" data-action="start">Build My Routine →</button>
        </div>
      `;
    }

    if (st >= 1 && st <= 4) {
      const titles = {
        1: "What do you want to support right now?",
        2: "Which sounds most like your daily life?",
        3: "When would you take your products?",
        4: "How many products do you want in your routine?"
      };
      const descs = {
        1: "Choose the top priority for this season",
        2: "This helps us match options that fit your pace",
        3: "Timing is strict: we only show matching items",
        4: "Consistency matters — pick what feels realistic"
      };

      const opts = st === 1 ? GOALS : st === 2 ? LIFESTYLES : st === 3 ? TIMINGS : COMMITS;
      const key = st === 1 ? "goal" : st === 2 ? "lifestyle" : st === 3 ? "timing" : "commit";
      const cols2 = st === 1 || st === 2;

      return `
        <div class="rp-stepwrap">
          <div class="rp-stephead">
            <h2 id="rp-step-h2">${esc(titles[st])}</h2>
            <p>${esc(descs[st])}</p>
          </div>

          <div class="rp-grid ${cols2 ? "" : "rp-grid-1"}">
            ${opts.map(o => renderOpt(o, key)).join("")}
          </div>

          ${st > 1 ? `<div class="rp-back"><button data-action="back">← Go back</button></div>` : ""}
        </div>
      `;
    }

    if (st === 5) {
      return `
        <div class="rp-stepwrap">
          <div class="rp-stephead">
            <h2 id="rp-step-h2">Do you prefer a specific product type?</h2>
            <p>Format is strict: we only show that format</p>
          </div>

          <div class="rp-grid">
            ${FORMATS.map(o => renderOpt(o, "format")).join("")}
          </div>

          <div class="rp-back"><button data-action="back">← Go back</button></div>
        </div>
      `;
    }

    if (st === 6) {
      return `
        <div class="rp-stepwrap">
          <div class="rp-stephead">
            <h2 id="rp-step-h2">Subscribe and continue</h2>
            <p>Enter your email, check the consent box, then click Continue. Or press Skip.</p>
          </div>

          <div class="rp-formcard">
            <div class="rp-omniwrap">
              <div id="rp-omni-slot"></div>
            </div>

            <div class="rp-formactions">
              <button class="rp-secondary" data-action="omni_skip">Skip</button>
            </div>
          </div>
        </div>
      `;
    }

    if (st === 7) {
      return `
        <div class="rp-loading">
          <h2>Building your routine...</h2>
          <p>Matching from ${PRODUCTS.length}+ products</p>
          <div class="rp-dots"><span></span><span></span><span></span></div>
        </div>
      `;
    }

    const pill = `🌿 ${cL[state.ans.commit] || ""} · Timing: ${tMap[state.ans.timing] || ""} · Format: ${fMap[state.ans.format] || ""}`;

    if (!state.results.length) {
      return `
        <div class="rp-results">
          <div class="rp-results-head">
            <h2>Your Routine Suggestions</h2>
            <p>No matches with your current timing + format filters.</p>
            <div class="rp-pill">${esc(pill)}</div>
          </div>

          <div class="rp-pcard">
            <div class="rp-pinner">
              <div style="flex:1;min-width:260px">
                <div style="font-weight:800;font-size:16px;margin-bottom:8px;">No matches found</div>
                <div style="font-size:14px;line-height:1.55;color:rgba(31,42,42,.75);margin-bottom:14px;">
                  Try switching <strong>Format</strong> to <strong>No Preference</strong>, or adjust your choices.
                </div>
                <div style="display:flex;gap:10px;flex-wrap:wrap;">
                  <button class="rp-primary-sm" data-action="set_format_any">Set “No Preference”</button>
                  <button class="rp-secondary" data-action="goto" data-step="5">Go back to Format</button>
                  <button class="rp-secondary" data-action="restart">Start Over</button>
                </div>
              </div>
            </div>
          </div>

          <div class="rp-disclaimer">
            <strong>Disclaimer:</strong> These suggestions are for general wellness and are not intended to diagnose, treat, cure, or prevent any disease.
          </div>
        </div>
      `;
    }

    return `
      <div class="rp-results">
        <div class="rp-results-head">
          <h2>Your Routine Suggestions</h2>
          <p>Based on your preference for "${esc(GL[state.ans.goal] || "")}", here are suggested picks:</p>
          <div class="rp-pill">${esc(pill)}</div>
        </div>

        <div class="rp-cards">
          ${state.results.map((p, i) => renderPCard(p, i, state.ans.goal)).join("")}
        </div>

        <div class="rp-disclaimer">
          <strong>Disclaimer:</strong> These suggestions are for general wellness and are not intended to diagnose, treat, cure, or prevent any disease.
        </div>

        <div class="rp-restart">
          <button class="rp-secondary" data-action="restart">← Start Over</button>
        </div>
      </div>
    `;
  }

  function renderOpt(o, k) {
    const sel = state.ans[k] === o.id;
    return `
      <div class="rp-opt ${sel ? "is-selected" : ""}" role="button" tabindex="0"
           data-action="pick" data-key="${esc(k)}" data-id="${esc(o.id)}">
        <div class="rp-opt-ico">${esc(o.i)}</div>
        <div class="rp-opt-body">
          <div class="rp-opt-title">${esc(o.l)}</div>
          <div class="rp-opt-desc">${esc(o.d)}</div>
        </div>
        <div class="rp-check ${sel ? "is-selected" : ""}">
          ${sel ? `<svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M20 6L9 17l-5-5" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>` : ``}
        </div>
      </div>
    `;
  }

  function renderPCard(p, rank, goal) {
    const added = !!state.addedMap[p.n];
    const busy = !!state.busyMap[p.n];
    const err = state.errorMap[p.n] || "";

    const btnLabel = busy ? "Adding..." : added ? "✓ Added!" : err ? "Try again" : "Add to Cart";
    const btnDisabled = busy ? "disabled" : "";

    const handle = getHandle(p.u);
    const meta = handle ? PRODUCT_CACHE.get(handle) : null;
    const img = meta?.img || null;
    const price = typeof meta?.price === "number" ? `$${meta.price.toFixed(2)}` : "$24.99";
    const matchPct = typeof p.matchPct === "number" ? p.matchPct : 80;

    const utm = buildRpUtm(state.ans);
    const viewUrl = withUtm(p.u, utm);

    return `
      <div class="rp-pcard" data-product="${esc(p.n)}">
        <div class="rp-pinner">
          <div class="rp-thumb">
            ${img ? `<img class="rp-img" src="${esc(img)}" alt="${esc(p.n)}" loading="lazy">` : `<span class="rp-thumb-ico">🌿</span>`}
            <span class="rp-badge">${esc(FL[p.f] || "")}</span>
          </div>

          <div class="rp-pbody">
            <div class="rp-ptop">
              <div class="rp-rank">${rank + 1}</div>
              <div class="rp-pname">${esc(p.n)}</div>
              <div class="rp-match">${matchPct}%</div>
            </div>

            <div class="rp-tagsline">
              <span class="rp-tag ${p.f === "b" ? "is-warn" : "is-green"}">${esc(FL[p.f] || "")}</span>
              <span class="rp-price">${esc(price)}</span>
            </div>

            <div class="rp-goals">
              ${p.g.map((g) => `<span class="rp-chip ${g === goal ? "is-goal" : ""}">${esc(GL[g] || g)}</span>`).join("")}
            </div>

            <div class="rp-time">${esc(TL[p.t] || "⏰ Take anytime")}</div>

            ${err ? `<div class="rp-err">${esc(err)}</div>` : ""}

            <div class="rp-actions">
              <button class="rp-primary-sm" ${btnDisabled} data-action="add" data-name="${esc(p.n)}">${btnLabel}</button>
              <a class="rp-outline" href="${esc(viewUrl)}" target="_blank" rel="noopener noreferrer">View</a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function go(n) {
    shouldAutoScroll = true;

    if (n === 7) {
      state.step = 7;
      render();

      window.setTimeout(() => {
        state.results = calc(state.ans);
        state.step = 8;

        const uid = getAnonId();

        // Track in dataLayer/GA4
        pushDL("rp_complete", {
          rp_uid: uid,
          rp_goal: state.ans.goal,
          rp_lifestyle: state.ans.lifestyle,
          rp_timing: state.ans.timing,
          rp_format: state.ans.format,
          rp_commit: state.ans.commit,
          rp_results: (state.results || []).map((x) => `${x.n}:${x.matchPct ?? ""}`).join("|")
        });

        // Build & send Omnisend quiz_completed with images/prices
        (async () => {
          // If email exists, make sure contact is identified (helps event attach to contact)
          if (state.lastEmail) {
            omniIdentify(state.lastEmail, {
              rp_goal: state.ans.goal,
              rp_lifestyle: state.ans.lifestyle,
              rp_timing: state.ans.timing,
              rp_commit: state.ans.commit,
              rp_format: state.ans.format,
              rp_uid: uid,
              rp_page: String(location.href || '')
            });
          }

          const payload = await buildQuizCompletedPayload(uid);
          omniTrack("quiz_completed", payload);
        })();

        render();

        // Preload images/prices + warm variant cache
        state.results.forEach((p) => {
          const h = getHandle(p.u);
          if (!h) return;
          getProductMetaByHandle(h).then(() => render()).catch(() => {});
          getVariantIdByHandle(h).catch(() => {});
        });
      }, 650);
    } else {
      state.step = n;
      render();
    }
  }

  function pick(k, v) {
    state.ans[k] = v;
    pushDL("rp_answer", { rp_uid: getAnonId(), rp_step: state.step, rp_key: k, rp_value: v });

    const nx = { goal: 2, lifestyle: 3, timing: 4, commit: 5, format: 6 };
    window.setTimeout(() => go(nx[k]), 220);
    render();
  }

  function back() {
    shouldAutoScroll = true;
    if (state.step > 1) go(state.step - 1);
  }

  function restart(){
    try{
      const u = new URL(window.location.href);
      window.location.href = u.toString();
    }catch{
      window.location.reload();
    }
  }

  function gotoStep(n) {
    shouldAutoScroll = true;
    state.step = n;
    render();
  }

  function setTempAdded(name, ms) {
    state.addedMap[name] = true;
    render();
    window.setTimeout(() => { delete state.addedMap[name]; render(); }, ms);
  }

  function setTempError(name, message, ms) {
    state.errorMap[name] = message;
    render();
    window.setTimeout(() => { if (state.errorMap[name] === message) delete state.errorMap[name]; render(); }, ms);
  }

  async function addToCart(name) {
    if (state.busyMap[name]) return;

    const prod = PRODUCTS.find((p) => p.n === name) || null;
    if (!prod) return setTempError(name, "Product not found", 3500);

    const handle = getHandle(prod.u);
    if (!handle) return setTempError(name, "Invalid product URL", 3500);

    pushDL("rp_add_to_cart_click", { rp_uid: getAnonId(), rp_product: name });

    state.busyMap[name] = true;
    delete state.errorMap[name];
    render();

    try {
      const variantId = await getVariantIdByHandle(handle);
      await shopifyAddToCart(variantId, 1);

      pushDL("rp_add_to_cart_success", { rp_uid: getAnonId(), rp_product: name });

      delete state.busyMap[name];
      setTempAdded(name, 1800);
    } catch (err) {
      pushDL("rp_add_to_cart_error", { rp_uid: getAnonId(), rp_product: name });
      delete state.busyMap[name];
      setTempError(name, "Add to cart failed", 4500);
    }
  }

  function wireEvents(root) {
    root.onclick = (e) => {
      const viewLink = e.target.closest("a.rp-outline");
      if (viewLink) {
        const card = viewLink.closest(".rp-pcard");
        const name = card?.getAttribute("data-product") || "";
        pushDL("rp_view", { rp_uid: getAnonId(), rp_product: name });
      }

      const el = e.target.closest("[data-action]");
      if (!el) return;
      const a = el.getAttribute("data-action");

      if (a === "start") { pushDL("rp_start", { rp_uid: getAnonId() }); return go(1); }
      if (a === "back") return back();
      if (a === "restart") return restart();

      if (a === "goto") {
        const s = parseInt(el.getAttribute("data-step") || "", 10);
        if (Number.isFinite(s)) gotoStep(s);
        return;
      }

      if (a === "retry_data") return initData();

      if (a === "set_format_any") { state.ans.format = "any"; return go(7); }

      if (a === "omni_skip") { pushDL("rp_email_skip", { rp_uid: getAnonId() }); return go(7); }

      if (a === "pick") {
        const k = el.getAttribute("data-key");
        const id = el.getAttribute("data-id");
        if (k && id) pick(k, id);
        return;
      }

      if (a === "add") {
        const name = el.getAttribute("data-name");
        if (name) addToCart(name);
        return;
      }
    };

    root.onkeydown = (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const el = e.target.closest('[data-action="pick"]');
      if (!el) return;
      e.preventDefault();
      const k = el.getAttribute("data-key");
      const id = el.getAttribute("data-id");
      if (k && id) pick(k, id);
    };
  }

  async function initData() {
    state.dataError = "";
    render();

    try {
      ensureGlobalMountVisibleNotDisplayNone();

      const r = await fetch(DATA_ASSET_URL, { credentials: "same-origin" });
      if (!r.ok) throw new Error("Cannot fetch routine-products.json from Assets");
      const j = await r.json();
      const list = Array.isArray(j.products) ? j.products : Array.isArray(j) ? j : [];
      if (!list.length) throw new Error("routine-products.json has no products");
      PRODUCTS = list;
      render();
    } catch (e) {
      state.dataError = e && e.message ? e.message : "Failed to load data";
      render();
    }
  }

  function init() {
    const root = qs("#herbera-routine-root");
    if (!root) return;
    initData();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();